/**
 * DOS518 — Integrazione ASTUTO
 * mapper.ts — Converte dati ASTUTO nei tipi DOS518 e scrive sullo store.
 *
 * applyAstutoImport() chiama useAppStore.getState() direttamente
 * (stesso pattern di QRCambioDOS.tsx) per essere callable fuori da React.
 */

import { useAppStore } from '../../store/useAppStore';
import type { Qualifica, TipoMezzoTerrestre, TipoMezzoAereo } from '../../types';
import type { AstutoIntervento, ImportOptions } from './types';

// ── Mapping qualifiche ASTUTO → DOS518 ──────────────────────────────────────

export function mapQualifica(q?: string): Qualifica {
  if (!q) return 'operatore';
  const s = q.toLowerCase();
  if (s.includes('capo') && s.includes('squadra')) return 'capo_squadra';
  if (s.includes('cs'))                             return 'capo_squadra';
  if (s.includes('autista') && (s.includes('1') || s.includes('i°') || s.includes('1°'))) return 'autista_1_grado';
  if (s.includes('autista') && (s.includes('2') || s.includes('ii°')))                   return 'autista_2_grado';
  if (s.includes('autista') && (s.includes('3') || s.includes('iii°')))                  return 'autista_3_grado';
  if (s.includes('autista') && (s.includes('4') || s.includes('iv°')))                   return 'autista_4_grado';
  if (s.includes('autista'))                        return 'autista_1_grado'; // fallback autista generico
  if (s.includes('volontario') || s.includes('aib') || s.includes('vol')) return 'volontario';
  return 'operatore';
}

// ── Mapping tipo mezzo terrestre ─────────────────────────────────────────────

export function mapTipoMezzo(t?: string): TipoMezzoTerrestre {
  if (!t) return 'altro';
  const s = t.toLowerCase();
  if (s.includes('autobotte') || s.includes('botte'))   return 'autobotte';
  if (s.includes('aps'))                                 return 'aps';
  if (s.includes('pick') || s.includes('fuoristrada'))   return 'pick_up';
  if (s.includes('boschivo'))                            return 'modulo_boschivo';
  if (s.includes('squadra') && s.includes('aib'))        return 'squadra_aib';
  if (s.includes('squadra'))                             return 'squadra';
  return 'altro';
}

// ── Mapping tipo mezzo aereo ─────────────────────────────────────────────────

export function mapTipoMezzoAereo(t?: string): TipoMezzoAereo {
  if (!t) return 'ala_rotante';
  const s = t.toLowerCase();
  if (s.includes('elicottero') || s.includes('heli') || s.includes('rotante')) return 'ala_rotante';
  if (s.includes('canadair') || s.includes('fisso') || s.includes('aereo'))    return 'ala_fissa';
  return 'ala_rotante';
}

// ── Importazione principale ──────────────────────────────────────────────────

/**
 * Scrive i dati ASTUTO nello store Zustand in base alle opzioni selezionate.
 * Restituisce un riepilogo testuale per il messaggio di successo.
 */
export function applyAstutoImport(
  dati: AstutoIntervento,
  options: ImportOptions
): string {
  const store = useAppStore.getState();
  const contatori = { personale: 0, mezziT: 0, mezziA: 0 };

  if (options.svuotaEsistenti) {
    store.resetData();
  }

  // Personale
  if (options.importaPersonale) {
    dati.personale.forEach((p) => {
      if (p.nome || p.cognome) {
        store.addPersonale({
          nome:      p.nome     || '',
          cognome:   p.cognome  || '',
          telefono:  p.telefono || '',
          qualifica: mapQualifica(p.qualifica),
          radio:     p.radio    || p.matricola || undefined,
        });
        contatori.personale++;
      }
    });
  }

  // Mezzi Terrestri
  if (options.importaMezziTerrestri) {
    dati.mezziTerrestri.forEach((m) => {
      if (m.sigla) {
        store.addMezzoTerrestre({
          sigla:              m.sigla,
          tipo:               mapTipoMezzo(m.tipo),
          targa:              m.targa     || undefined,
          ente:               m.ente      || undefined,
          posizioneOperativa: 'disponibile',
        });
        contatori.mezziT++;
      }
    });
  }

  // Mezzi Aerei
  if (options.importaMezziAerei) {
    dati.mezziAerei.forEach((m) => {
      if (m.sigla) {
        store.addMezzoAereo({
          sigla:              m.sigla,
          tipo:               mapTipoMezzoAereo(m.tipo),
          modello:            m.modello   || undefined,
          posizioneOperativa: 'disponibile',
        });
        contatori.mezziA++;
      }
    });
  }

  // Coordinate e dati COAU
  if (options.importaCoordinate || options.importaCOAU) {
    const aggiornamenti: Record<string, any> = {};
    if (dati.comune)      aggiornamenti['localizzazione']    = dati.comune;
    if (dati.localita)    aggiornamenti['localita']          = dati.localita;
    if (dati.latitudine)  aggiornamenti['latitudine']        = dati.latitudine;
    if (dati.longitudine) aggiornamenti['longitudine']       = dati.longitudine;
    if (dati.codice)      aggiornamenti['codiceIntervento']  = dati.codice;
    if (Object.keys(aggiornamenti).length > 0) {
      store.updateCOAU(aggiornamenti);
    }
  }

  // Voce diario
  const isDemo = dati.codice?.startsWith('DEMO-') ?? false;
  const riepilogo = [
    contatori.personale > 0 ? `${contatori.personale} operatori` : '',
    contatori.mezziT    > 0 ? `${contatori.mezziT} mezzi terrestri` : '',
    contatori.mezziA    > 0 ? `${contatori.mezziA} mezzi aerei` : '',
  ].filter(Boolean).join(', ');

  store.addVoceDiario(
    'astuto',
    '🌲',
    `Import ASTUTO${isDemo ? ' (DEMO)' : ''}: ${riepilogo || 'nessun dato importato'}${dati.comune ? ` — ${dati.comune}` : ''}`,
  );

  return riepilogo;
}
